import React, { useState, useEffect } from 'react';
import { useApp } from '../store';
import { UserRole } from '../types';
import { 
  LayoutDashboard, 
  Layers, 
  Users, 
  Trophy, 
  LogOut, 
  Briefcase,
  ChevronRight,
  Bell,
  Search,
  Archive,
  ClipboardList,
  RefreshCcw,
  Loader2,
  Globe,
  Rocket,
  Power,
  Lock,
  X,
  Key
} from 'lucide-react';
import Dashboard from '../pages/Dashboard';
import TeamMembers from '../pages/TeamMembers';
import Leaderboard from '../pages/Leaderboard';
import CompletedProjects from '../pages/CompletedProjects';
import ProjectsOverview from '../pages/ProjectsOverview';
import DeploymentGuide from '../pages/DeploymentGuide';
import { DailyReportModal } from './DailyReportModal';

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
  onClick: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ icon, label, isActive, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
      isActive 
        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200' 
        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
    }`}
  >
    {icon}
    <span className="font-medium text-sm">{label}</span>
    {isActive && <ChevronRight className="ml-auto w-4 h-4" />}
  </button>
);

const ChangePasswordModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { currentUser, updateUserPassword } = useApp();
  const [newPassword, setNewPassword] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 4) {
      alert("Password must be at least 4 characters long.");
      return;
    }
    if (!currentUser) return;

    setIsUpdating(true);
    await updateUserPassword(currentUser.id, newPassword);
    setIsUpdating(false);
    alert("Password updated successfully!");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600">
              <Key className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Security</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <input 
                required
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-bold text-slate-900"
              />
            </div>
          </div>
          <button 
            type="submit" 
            disabled={isUpdating}
            className="w-full py-4 bg-indigo-600 text-white font-black text-[10px] uppercase tracking-widest rounded-xl shadow-lg shadow-indigo-100 hover:shadow-indigo-300 transition-all disabled:opacity-50"
          >
            {isUpdating ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isSyncing, isLoading, logout } = useApp();
  const [currentView, setCurrentView] = useState<'dashboard' | 'overview' | 'team' | 'leaderboard' | 'completed' | 'deploy'>('dashboard');
  const [showDailyReport, setShowDailyReport] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    const hasSeenToday = localStorage.getItem(`daily_brief_${new Date().toDateString()}`);
    if (!hasSeenToday && !isLoading) {
      setTimeout(() => setShowDailyReport(true), 1500);
      localStorage.setItem(`daily_brief_${new Date().toDateString()}`, 'true');
    }
  }, [isLoading]);

  const renderView = () => {
    if (isLoading) return (
      <div className="h-full flex flex-col items-center justify-center space-y-4">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
        <p className="text-xs font-black text-slate-400 uppercase tracking-widest animate-pulse">Establishing Secure Connection...</p>
      </div>
    );

    switch(currentView) {
      case 'overview': return <ProjectsOverview />;
      case 'team': return <TeamMembers />;
      case 'leaderboard': return <Leaderboard />;
      case 'completed': return <CompletedProjects />;
      case 'deploy': return <DeploymentGuide />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="w-64 border-r border-slate-200 bg-white flex flex-col hidden lg:flex">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-100">N</div>
            <h1 className="font-bold text-xl tracking-tight text-slate-900 uppercase">Nice Digital</h1>
          </div>
          <div className="flex items-center gap-2 mt-1">
            {isSyncing ? (
              <RefreshCcw className="w-3 h-3 text-indigo-500 animate-spin" />
            ) : (
              <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-sm shadow-emerald-200"></div>
            )}
            <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">
              {isSyncing ? 'Cloud Syncing...' : 'Connected • Secure'}
            </p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <SidebarItem 
            icon={<LayoutDashboard className="w-5 h-5" />} 
            label="Active Dashboard" 
            isActive={currentView === 'dashboard'} 
            onClick={() => setCurrentView('dashboard')} 
          />
          <SidebarItem 
            icon={<ClipboardList className="w-5 h-5" />} 
            label="Projects Overview" 
            isActive={currentView === 'overview'} 
            onClick={() => setCurrentView('overview')} 
          />
          {currentUser?.role === UserRole.ADMIN && (
            <SidebarItem 
              icon={<Users className="w-5 h-5" />} 
              label="Team Management" 
              isActive={currentView === 'team'} 
              onClick={() => setCurrentView('team')} 
            />
          )}
          <SidebarItem 
            icon={<Trophy className="w-5 h-5" />} 
            label="Leaderboard" 
            isActive={currentView === 'leaderboard'} 
            onClick={() => setCurrentView('leaderboard')} 
          />
          <SidebarItem 
            icon={<Archive className="w-5 h-5" />} 
            label="Completed Projects" 
            isActive={currentView === 'completed'} 
            onClick={() => setCurrentView('completed')} 
          />
        </nav>

        <div className="p-4 border-t border-slate-100 bg-slate-50/50">
          <button 
            onClick={() => setShowPasswordModal(true)}
            className="w-full mb-3 flex items-center gap-3 p-2 bg-white rounded-xl shadow-sm border border-slate-200 hover:border-indigo-300 transition-all text-left group"
          >
            <img src={currentUser?.avatar} className="w-10 h-10 rounded-full border border-slate-200 shadow-sm" alt="User" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-slate-900 truncate tracking-tight group-hover:text-indigo-600 transition-colors">{currentUser?.name.split(' ')[0]}</p>
              <p className="text-[9px] font-bold text-indigo-600 uppercase tracking-widest opacity-80">Settings</p>
            </div>
          </button>
          <button 
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors group"
          >
            <LogOut className="w-4 h-4 group-hover:scale-110 transition-transform" />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-10">
          <div className="flex items-center gap-6 flex-1">
            <div className="lg:hidden w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold">N</div>
            <div className="relative max-w-md w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-300" />
              <input 
                type="text" 
                placeholder="Search Database..." 
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all font-bold text-slate-700 placeholder:font-medium placeholder:text-slate-300 shadow-inner"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button 
              onClick={() => setShowDailyReport(true)}
              className="relative p-3 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-2xl transition-all group shadow-sm"
              title="Daily Briefing"
            >
              <Bell className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              <span className="absolute top-1 right-1 w-3 h-3 bg-red-500 border-2 border-white rounded-full"></span>
            </button>

            <button 
              onClick={() => setShowPasswordModal(true)}
              className="p-3 bg-slate-50 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 rounded-2xl transition-all group shadow-sm border border-slate-100"
              title="Change Password"
            >
              <Lock className="w-5 h-5" />
            </button>

            <button 
              onClick={logout}
              className="p-3 bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-2xl transition-all group shadow-sm border border-slate-100"
              title="Sign Out"
            >
              <Power className="w-5 h-5 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-8 bg-slate-50/30">
          {renderView()}
        </div>
      </main>

      {showDailyReport && <DailyReportModal onClose={() => setShowDailyReport(false)} />}
      {showPasswordModal && <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />}
    </div>
  );
};